import React from 'react';
import { themes, useTheme } from '../context/ThemeContext';
import DashboardLayout from '../components/DashboardLayout';
import { useFieldLabel } from '../hooks/useFieldLabel';

const ThemeSettings: React.FC = () => {
  const { theme, themeName, setTheme, availableThemes } = useTheme();
  const themeSettingsLabel = useFieldLabel('MENU_THEME_SETTINGS', 'Theme Settings');

  const handleThemeChange = (newTheme: string) => {
    setTheme(newTheme);
  };

  return (
    <DashboardLayout title={themeSettingsLabel}>
      <div className="max-w-2xl mx-auto">
        <p className="mb-0 max-w-2xl text-sm leading-6 text-gray-600 sm:text-base" style={{ color: theme.textSecondary }}>
          Choose a theme once and the shared colors across the UI will follow it automatically.
        </p>

        <div className="space-y-6">
          <div>
            <h2 className="text-xl font-semibold mb-4" style={{ color: theme.text }}>
              Choose Theme
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {availableThemes.map((themeOption) => {
                const previewTheme = themes[themeOption] ?? themes.light;
                return (
                  <button
                    key={themeOption}
                    onClick={() => handleThemeChange(themeOption)}
                    className="p-4 rounded-lg border-2 transition-all duration-200 text-left"
                    style={{
                      backgroundColor: previewTheme.surface,
                      color: previewTheme.text,
                      borderColor: themeName === themeOption ? theme.primary : previewTheme.border,
                      boxShadow: themeName === themeOption ? `0 0 0 2px ${theme.primaryLight}` : undefined
                    }}
                  >
                    <div>
                      <h3 className="font-medium capitalize">{themeOption.replace('-', ' ')}</h3>
                      <p className="mt-1 text-sm" style={{ color: previewTheme.textSecondary }}>
                        {previewTheme.name} palette
                      </p>
                      <div className="mt-3 flex space-x-2">
                        <div
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: previewTheme.primary }}
                        ></div>
                        <div
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: previewTheme.secondary }}
                        ></div>
                        <div
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: previewTheme.accent }}
                        ></div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-4" style={{ color: theme.text }}>
              Current Theme Preview
            </h2>
            <div
              className="p-6 rounded-lg border"
              style={{ backgroundColor: theme.surface, borderColor: theme.border }}
            >
              <div className="space-y-4">
                <div>
                  <h3 style={{ color: theme.text }}>Sample Text</h3>
                  <p style={{ color: theme.textSecondary }}>
                    This is how text will appear with the current theme.
                  </p>
                </div>
                <div className="flex space-x-4">
                  <button
                    className="px-4 py-2 rounded"
                    style={{ backgroundColor: theme.primary, color: theme.contrastText }}
                  >
                    Primary Button
                  </button>
                  <button
                    className="px-4 py-2 rounded border"
                    style={{
                      backgroundColor: theme.surface,
                      color: theme.text,
                      borderColor: theme.border
                    }}
                  >
                    Secondary Button
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ThemeSettings;
